import localeDa from '@angular/common/locales/da';
import { enableProdMode, ErrorHandler, importProvidersFrom, LOCALE_ID } from '@angular/core';

import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { MsalBroadcastService, MsalGuard, MsalInterceptor, MsalModule, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { WorkspaceComponent } from 'components/core/workspace/workspace.component';
import { DropdownListService } from 'components/input/custom-select/dropdown-list';
import { AutosizeModule } from 'ngx-autosize';
import { CurrencyPipe } from 'pipes/currency.pipe';
import { CustomDatePipe } from 'pipes/custom-date.pipe';
import { PaymentIntervalPipe } from 'pipes/payment-interval.pipe';
import { PhoneNumberPipe } from 'pipes/phone-number.pipe';
import { ProductNamePipe } from 'pipes/product-name.pipe';
import { TeamPipe } from 'pipes/team.pipe';
import { TranslatePipe } from 'pipes/translate.pipe';
import { UserPipe } from 'pipes/user.pipe';
import { AccountingService } from 'services/accounting.service';
import { AiBuilderService } from 'services/ai-builder.service';
import { InvoicingService } from 'services/invoicing.service';
import { CommunicationService } from 'services/communication.service';
import { CompanyService } from 'services/company.service';
import { CredentialService } from 'services/credential.service';
import { CspService } from 'services/csp.service';
import { DateService } from 'services/date.service';
import { DebtCollectionService } from 'services/debt-collection.service';
import { DomainService } from 'services/domain.service';
import { FileStorageService } from 'services/file-storage.service';
import { GlobalErrorHandlerService } from 'services/global-error-handler.service';
import { LanguageService } from 'services/language.service';
import { LeadsService } from 'services/leads.service';
import { LoggingService } from 'services/logging.service';
import { MailService } from 'services/mail.service';
import { NavigationService } from 'services/navigation.service';
import { NotificationService } from 'services/notification.service';
import { NumberService } from 'services/number.service';
import { PoolService } from 'services/pool.service';
import { PriceAdjustmentService } from 'services/price-adjustment.service';
import { ProductService } from 'services/product.service';
import { ProductPlanService } from 'services/productplan.service';
import { PushService } from 'services/push.service';
import { QueryService } from 'services/query.service';
import { QuestionnaireService } from 'services/questionnaire.service';
import { ReasonService } from 'services/reason.service';
import { SalesService } from 'services/sales.service';
import { LegalCaseService } from 'services/legal-case.service';
import { SiteService } from 'services/site.service';
import { SortableService } from 'services/sortable.service';
import { SyncService } from 'services/sync.service';
import { TaskService } from 'services/task.service';
import { TaskflowTemplateService } from 'services/taskflow-template.service';
import { UserService } from 'services/user.service';
import { AppHttpInterceptor } from './app/app-http-intercepter';
import { AppComponent } from 'components/core/app/app.component';
import { GormIdentityInterceptor } from './app/modules/gorm-identity/gorm-identity-interceptor';
import { GormIdentityModule } from './app/modules/gorm-identity/gorm-identity.module';
import { environment } from './environments/environment';
import { screenUrl } from 'components/core/screen-authenticator/screen-authenticator.component';
import { DnsLookupService } from 'services/dns-lookup.service';
import { NavigateToNotificationComponent } from 'components/core/notifications/navigate-to-notification.component';
import { BrandService } from 'services/brand.service';
import { DashboardService } from 'services/dashboard.service';

if (environment.production == "true") {
  enableProdMode();
}

const pipes = [
	TranslatePipe,
	CustomDatePipe,
	CurrencyPipe,
	DatePipe,
	PhoneNumberPipe,
	PaymentIntervalPipe,
	UserPipe,
	TeamPipe,
	ProductNamePipe
];

const matFormFieldDefaultOptions: MatFormFieldDefaultOptions = {
	appearance: 'outline',
	floatLabel: 'always',
	subscriptSizing: 'dynamic'
};
function getBaseUrl() {
	return document.getElementsByTagName('base')[0].href;
}

const services = [
	CommunicationService,
	MailService,
	CompanyService,
	CredentialService,
	DateService,
	DomainService,
	CspService,
	LanguageService,
	NavigationService,
	DropdownListService,
	NotificationService,
	ProductService,
	QueryService,
	QuestionnaireService,
	SalesService,
	SiteService,
	SortableService,
	TaskService,
	PushService,
	TaskflowTemplateService,
	PoolService,
	UserService,
	LeadsService,
	ProductPlanService,
	AccountingService,
	LoggingService,
	NumberService,
	PriceAdjustmentService,
	InvoicingService,
	ReasonService,
	SyncService,
	DebtCollectionService,
	FileStorageService,
	LegalCaseService,
	AiBuilderService,
	MsalService,
	MsalGuard,
	MsalBroadcastService,
	DnsLookupService,
	BrandService,
	DashboardService,
];

registerLocaleData(localeDa);

bootstrapApplication(AppComponent, {
	providers: [
		pipes,
		services,
		NavigateToNotificationComponent, // Needs to be loaded during bootstrap for the component URL to work without any menus pointing to it
		provideNativeDateAdapter(),
		provideHttpClient(withInterceptorsFromDi()),
		{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: matFormFieldDefaultOptions },
		{ provide: 'BASE_URL', useFactory: getBaseUrl },
		{ provide: LOCALE_ID, useValue: 'da-DK' },
		{ provide: ErrorHandler, useClass: GlobalErrorHandlerService },
		{ provide: HTTP_INTERCEPTORS, useClass: AppHttpInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
		{ provide: HTTP_INTERCEPTORS, useClass: GormIdentityInterceptor, multi: true },
		{ provide: DateAdapter, useClass: CustomDatePipe, deps: [TranslatePipe, DateService] },
		provideRouter([{
			path: '',
			canActivate: [MsalGuard],
			resolve: [LanguageService, LoggingService, UserService, BrandService],
			children: [{ path: '**', component: WorkspaceComponent }]
		}]),
		importProvidersFrom(
			BrowserModule,
			CommonModule,
			FormsModule,
			ReactiveFormsModule,
			AutosizeModule,
			GormIdentityModule,
			MsalModule.forRoot(new PublicClientApplication(
				{
					auth: {
						clientId: environment.clientId,
						authority: environment.authority,
						redirectUri: '/',
					},
					cache: {
						cacheLocation: BrowserCacheLocation.LocalStorage
					},
					system: {
						loggerOptions: {
							loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
								if (level === LogLevel.Error) {
									throw new Error(message);
								}
							},
							piiLoggingEnabled: false
						},
						allowRedirectInIframe: true
					}
				}),
				{
					interactionType: InteractionType.Redirect, // MSAL Guard Configuration
				},
				{
					interactionType: InteractionType.Redirect, // MSAL Interceptor Configuration
					protectedResourceMap: new Map([
						[environment.gatewayUrl, [environment.scope]],
						[screenUrl, [environment.scope]]
					])
				}
			),
			GormIdentityModule.forRoot({ tokenUrl: `${environment.gatewayUrl}/api/token`, matchUrls: [environment.gatewayUrl, environment.crmmigrationUrl] })
		)
	]
});
